import { ReactNode } from "react";

import {
  Notification,
  NotificationEntityType,
  NotificationType,
} from "@/apollo/types";
import IconWithBG from "@/components/elements/IconWithBG";
import TableIcon from "@/components/icons/TableIcon";
import { OrchestrationIcon } from "@/components/icons/domain-icons";
import type { MessageData } from "@/features/notifications";
import { IntegrationLogoBox } from "@/integrations";

function getTargetIntegrationId(
  notification: Notification,
): string | undefined {
  return notification.source.toLowerCase().includes("activate")
    ? notification.detail?.destinationIntegrationId
    : notification.detail?.sourceIntegrationId;
}

function getTitleProp(notification: Notification) {
  if (notification.entityType === NotificationEntityType.Materialisation) {
    return `Table update (${notification.entityName})`;
  }
  if (
    notification.entityType === NotificationEntityType.Sync ||
    notification.entityType === NotificationEntityType.Stream
  ) {
    return `${notification.detail.sourceIntegrationId} (${notification.entityName})`;
  }
  return notification.entityName ?? notification.accountName;
}

function getIconProp(notification: Notification) {
  if (notification.entityType === NotificationEntityType.Workflow) {
    return (
      <IconWithBG
        size="xs"
        icon={<OrchestrationIcon />}
        className="bg-gray-200 dark:bg-gray-700"
      />
    );
  }
  if (notification.entityType === NotificationEntityType.Materialisation) {
    return (
      <IconWithBG
        size="xs"
        icon={<TableIcon />}
        className="bg-gray-200 dark:bg-gray-700"
      />
    );
  }
  return (
    <IntegrationLogoBox
      size="xs"
      id={getTargetIntegrationId(notification) || ""}
    />
  );
}

const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

function getNotice(notification: Notification): ReactNode | undefined {
  if (notification.type !== NotificationType.MarUsageThresholdAlert) {
    return undefined;
  }
  const age = Date.now() - new Date(notification.lastOccurence).getTime();
  if (age < THREE_HOURS_MS) {
    return undefined;
  }
  return "The usage numbers in this notification may be outdated. Visit the Plan Details page for the latest figures.";
}

export function useNotificationItem(notification: Notification) {
  const messageData = notification.messageData as MessageData | undefined;
  return {
    id: notification.id,
    integrationId: getTargetIntegrationId(notification),
    title: messageData?.title ?? getTitleProp(notification),
    message: notification.message,
    alertLevel:
      notification.notificationLevel as `${Notification["notificationLevel"]}`,
    lastOccurence: new Date(notification.lastOccurence),
    icon: getIconProp(notification),
    messageData,
    notice: getNotice(notification),
  };
}
